import { useState, useMemo } from 'react';
import { PageHeader } from '@/components/layout/page-header';
import { MetricCard } from '@/components/ui/metric-card';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  SlideOverDrawer,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/slide-over-drawer';
import { useCDRMTD, useCDRTrend, useCDRDailyTrend, useCDRList, useCDRTopInbound, useCDRTopOutbound } from '@/features/dids';
import type { CDRTrendItem } from '@/features/dids';
import type { CDRDailyTrendItem } from '@/features/dids/api';
import {
  BarChart3,
  PhoneIncoming,
  PhoneOutgoing,
  Search,
  Download,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { cn, formatPhoneNumber } from '@/lib/utils';
import toast from 'react-hot-toast';

type TimeRange = 'daily' | '3m' | '6m' | '12m';

// Inbound call types - mirrors backend ORIG_CALL_TYPES
const INBOUND_CALL_TYPES = ['ORIG', 'Orig-Tiered', '800OrigE', '800ORIGE'];

// Format duration in minutes to mm:ss
function formatDuration(minutes: number): string {
  const totalSeconds = Math.round(minutes * 60);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

// Helper to get date N days ago in YYYY-MM-DD format
function getDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const parts = d.toISOString().split('T');
  return parts[0] || d.toISOString().slice(0, 10);
}

// Helper to get yesterday's date in YYYY-MM-DD format
// CDR data is imported daily at 6 AM for previous day, so today is always incomplete
function getYesterday(): string {
  return getDaysAgo(1);
}

// CDR Search Drawer component
interface CDRSearchDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  initialCallType?: 'ORIG' | 'TERM' | '';
}

function CDRSearchDrawer({ isOpen, onClose, initialCallType = '' }: CDRSearchDrawerProps) {
  // Default to last 7 completed days (yesterday through 7 days ago)
  // CDR data is imported at 6 AM for previous day, so today is always incomplete
  const [startDate, setStartDate] = useState(getDaysAgo(7));
  const [endDate, setEndDate] = useState(getYesterday());
  const [callType, setCallType] = useState<string>(initialCallType);
  const [remoteNumber, setRemoteNumber] = useState('');
  const [page, setPage] = useState(1);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);

  // Fetch CDR data with filters
  const { data: cdrData, isLoading } = useCDRList({
    start_date: startDate,
    end_date: endDate,
    call_type: callType || undefined,
    search: remoteNumber || undefined,
    page,
    page_size: 25,
  });

  // Calculate summary stats
  const summary = useMemo(() => {
    if (!cdrData) return { calls: 0, minutes: 0, avgDuration: '0:00' };
    const totalMinutes = cdrData.records.reduce((sum, r) => sum + r.bill_minutes, 0);
    const avgDuration = cdrData.records.length > 0 ? totalMinutes / cdrData.records.length : 0;
    return {
      calls: cdrData.total,
      minutes: Math.round(totalMinutes),
      avgDuration: formatDuration(avgDuration),
    };
  }, [cdrData]);

  // Export records to CSV
  const exportToCsv = (records: NonNullable<typeof cdrData>['records'], filename: string) => {
    const headers = ['Timestamp', 'Type', 'From', 'To', 'Duration', 'Cost'];
    const rows = records.map((r) => [
      new Date(r.start_time).toLocaleString(),
      INBOUND_CALL_TYPES.includes(r.call_type) ? 'INBOUND' : 'OUTBOUND',
      formatPhoneNumber(r.ani),
      formatPhoneNumber(r.dnis),
      formatDuration(r.bill_minutes),
      `$${r.call_price.toFixed(4)}`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export current page
  const handleExportCurrentPage = () => {
    if (!cdrData || cdrData.records.length === 0) {
      toast.error('No records to export');
      return;
    }
    exportToCsv(cdrData.records, `cdr-report-${startDate}-${endDate}-page${page}.csv`);
    toast.success(`Exported ${cdrData.records.length} records`);
    setExportDropdownOpen(false);
  };

  // Export all results - fetch all pages
  const handleExportAll = async () => {
    if (!cdrData || cdrData.total === 0) {
      toast.error('No records to export');
      return;
    }

    const MAX_EXPORT = 50000;
    const exportCount = Math.min(cdrData.total, MAX_EXPORT);

    setIsExportingAll(true);
    setExportDropdownOpen(false);

    try {
      // Fetch all records by requesting a large page size
      const { getCDRList } = await import('@/features/dids/api');
      const allData = await getCDRList({
        start_date: startDate,
        end_date: endDate,
        call_type: callType || undefined,
        search: remoteNumber || undefined,
        page: 1,
        page_size: exportCount,
      });

      exportToCsv(allData.records, `cdr-report-${startDate}-${endDate}-all.csv`);

      if (cdrData.total > MAX_EXPORT) {
        toast.success(`Exported ${allData.records.length.toLocaleString()} of ${cdrData.total.toLocaleString()} records (max ${MAX_EXPORT.toLocaleString()})`);
      } else {
        toast.success(`Exported ${allData.records.length.toLocaleString()} records`);
      }
    } catch (error) {
      toast.error('Failed to export all records');
    } finally {
      setIsExportingAll(false);
    }
  };

  return (
    <SlideOverDrawer isOpen={isOpen} onClose={onClose} width="2xl">
      <DrawerHeader title="Call Detail Records" onClose={onClose} />

      <DrawerBody className="flex flex-col">
        {/* Filter Ribbon */}
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Date Range */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-500 mb-1">Date Range</label>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  className="text-sm"
                />
                <span className="text-slate-400">-</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  className="text-sm"
                />
              </div>
            </div>

            {/* Call Type */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Call Type</label>
              <select
                value={callType}
                onChange={(e) => { setCallType(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All</option>
                <option value="ORIG">Inbound</option>
                <option value="TERM">Outbound</option>
              </select>
            </div>

            {/* Number Search */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Number Search</label>
              <Input
                type="text"
                placeholder="Search..."
                value={remoteNumber}
                onChange={(e) => { setRemoteNumber(e.target.value); setPage(1); }}
                className="text-sm"
              />
            </div>
          </div>
        </div>

        {/* CDR Forensic Summary Bar */}
        <div className="bg-slate-50 border-y border-slate-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="font-mono-data font-medium text-slate-900">{cdrData?.total.toLocaleString() || 0}</span>
              <span className="text-slate-500 ml-1">Calls</span>
            </div>
            <div className="h-4 w-px bg-slate-300" />
            <div>
              <span className="font-mono-data font-medium text-slate-900">{summary.minutes.toLocaleString()}</span>
              <span className="text-slate-500 ml-1">Minutes</span>
            </div>
            <div className="h-4 w-px bg-slate-300" />
            <div>
              <span className="font-mono-data font-medium text-slate-900">{summary.avgDuration}</span>
              <span className="text-slate-500 ml-1">Avg Duration</span>
            </div>
          </div>
          <div className="relative">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              disabled={isExportingAll}
            >
              {isExportingAll ? (
                <>
                  <Spinner size="sm" className="mr-1.5" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={14} className="mr-1.5" />
                  Export
                  <ChevronDown size={14} className="ml-1" />
                </>
              )}
            </Button>
            {exportDropdownOpen && (
              <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                <button
                  onClick={handleExportCurrentPage}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Current Page ({cdrData?.records.length || 0} records)
                </button>
                <button
                  onClick={handleExportAll}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  All Results ({cdrData?.total.toLocaleString() || 0} records)
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Results Table */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : cdrData && cdrData.records.length > 0 ? (
            <table className="w-full">
              <thead className="sticky top-0 bg-white border-b border-slate-100">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Timestamp</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Type</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">From</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">To</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Duration</th>
                </tr>
              </thead>
              <tbody>
                {cdrData.records.map((record) => {
                  const isInbound = INBOUND_CALL_TYPES.includes(record.call_type);
                  return (
                    <tr key={record.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4 text-sm text-slate-600 whitespace-nowrap">
                        {new Date(record.start_time).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}, {new Date(record.start_time).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true,
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <span className={cn(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold',
                          isInbound ? 'bg-indigo-50 text-indigo-700' : 'bg-purple-50 text-purple-700'
                        )}>
                          {isInbound ? 'INBOUND' : 'OUTBOUND'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm font-mono text-slate-600">{formatPhoneNumber(record.ani)}</td>
                      <td className="py-3 px-4 text-sm font-mono text-slate-600">{formatPhoneNumber(record.dnis)}</td>
                      <td className="py-3 px-4 text-sm text-slate-600 text-right tabular-nums">{formatDuration(record.bill_minutes)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Search size={32} className="text-slate-300 mb-3" />
              <p>No call records found matching your filters.</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {cdrData && cdrData.pages > 1 && (
          <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-slate-200 bg-white">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span className="text-sm text-slate-600">
              Page {page} of {cdrData.pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(cdrData.pages, p + 1))}
              disabled={page >= cdrData.pages}
              className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </DrawerBody>
    </SlideOverDrawer>
  );
}

export function UsagePage() {
  const [timeRange, setTimeRange] = useState<TimeRange>('6m');
  const [searchQuery, setSearchQuery] = useState('');
  const [callTypeFilter, setCallTypeFilter] = useState<string>('');
  const [cdrPage, setCdrPage] = useState(1);

  // CDR Drawer state
  const [cdrDrawerOpen, setCdrDrawerOpen] = useState(false);
  const [cdrDrawerCallType, setCdrDrawerCallType] = useState<'ORIG' | 'TERM' | ''>('');

  // Last 7 completed days date range (yesterday through 7 days ago)
  // CDR data is imported at 6 AM for previous day, so today is always incomplete
  const last7DaysStart = getDaysAgo(7);
  const yesterday = getYesterday();

  // Fetch CDR data
  const { data: mtdData, isLoading: mtdLoading } = useCDRMTD();
  const { data: trendData = [], isLoading: trendLoading } = useCDRTrend(12);
  const { data: dailyTrendData = [], isLoading: dailyTrendLoading } = useCDRDailyTrend(7);
  const { data: topInboundData, isLoading: topInboundLoading } = useCDRTopInbound();
  const { data: topOutboundData, isLoading: topOutboundLoading } = useCDRTopOutbound();

  // CDR list with filters
  const { data: cdrList, isLoading: cdrListLoading } = useCDRList({
    start_date: last7DaysStart,
    end_date: yesterday,
    call_type: callTypeFilter || undefined,
    search: searchQuery || undefined,
    page: cdrPage,
    page_size: 10,
  });

  // Format large numbers (e.g., 4200000 -> "4.2M")
  const formatLargeNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  // Format minutes with comma separation
  const formatMinutes = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  };

  // Transform CDR trend data for chart based on selected time range
  const chartData = useMemo(() => {
    if (timeRange === 'daily') {
      if (!dailyTrendData || dailyTrendData.length === 0) return [];
      return dailyTrendData.map((item: CDRDailyTrendItem) => ({
        time: item.day_name,
        minutes: Math.round(item.orig_minutes + item.term_minutes),
        inbound: Math.round(item.orig_minutes),
        outbound: Math.round(item.term_minutes),
      }));
    }

    if (!trendData || trendData.length === 0) return [];

    // Determine how many months to show based on time range
    const monthsToShow = timeRange === '3m' ? 3 : timeRange === '6m' ? 6 : 12;

    // Get the most recent N months of data
    const recentData = trendData.slice(-monthsToShow);

    return recentData.map((item: CDRTrendItem) => ({
      time: item.month_name.substring(0, 3), // Abbreviate month name
      minutes: Math.round(item.orig_minutes + item.term_minutes),
      inbound: Math.round(item.orig_minutes),
      outbound: Math.round(item.term_minutes),
    }));
  }, [trendData, dailyTrendData, timeRange]);

  // Handle search
  const handleSearch = () => {
    setCdrPage(1);
  };

  // Open CDR drawer with pre-filters
  const openCDRDrawer = (callType: 'ORIG' | 'TERM' | '' = '') => {
    setCdrDrawerCallType(callType);
    setCdrDrawerOpen(true);
  };

  const isLoading = mtdLoading || topInboundLoading || topOutboundLoading;

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Traffic & Usage" />
        <div className="flex items-center justify-center py-24">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  // Calculate totals
  const totalMinutes = mtdData?.total_minutes_mtd ?? 0;
  const inboundTotal = (mtdData?.orig_local_minutes_mtd ?? 0) + (mtdData?.orig_tf_minutes_mtd ?? 0);
  const outboundTotal = mtdData?.term_minutes_mtd ?? 0;

  return (
    <div>
      <PageHeader title="Traffic & Usage" />

      <div className="p-6 space-y-6">
        {/* Metric Cards Row - Horizontal variant */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard
            icon={BarChart3}
            label="Total Minutes"
            value={formatMinutes(totalMinutes)}
            subtitle="Last 30 days"
            variant="horizontal"
          />
          <MetricCard
            icon={PhoneIncoming}
            label="Inbound Total"
            value={formatLargeNumber(inboundTotal)}
            subtitle="Current Month"
            variant="horizontal"
          />
          <MetricCard
            icon={PhoneOutgoing}
            label="Outbound Total"
            value={formatLargeNumber(outboundTotal)}
            subtitle="Current Month"
            variant="horizontal"
          />
        </div>

        {/* Usage Trend Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Usage Trend</h2>
              <p className="text-sm text-slate-500">
                {timeRange === 'daily' ? 'Daily minute usage over the last 7 days' : 'Monthly minute usage over time'}
              </p>
            </div>
            <div className="flex bg-slate-100 rounded-lg p-1">
              {(['daily', '3m', '6m', '12m'] as TimeRange[]).map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                    timeRange === range
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  )}
                >
                  {range === 'daily' ? 'Daily' : range}
                </button>
              ))}
            </div>
          </div>

          <div className="h-[300px]">
            {(timeRange === 'daily' ? dailyTrendLoading : trendLoading) ? (
              <div className="flex items-center justify-center h-full">
                <Spinner />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorMinutesUsage" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) =>
                      value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value
                    }
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '12px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    }}
                    labelStyle={{ color: '#0f172a', fontWeight: 600 }}
                    formatter={(value) => [`${Number(value).toLocaleString()} mins`, 'Total']}
                  />
                  <Area
                    type="monotone"
                    dataKey="minutes"
                    stroke="#6366F1"
                    strokeWidth={2}
                    fill="url(#colorMinutesUsage)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Top DIDs Two-Column Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Inbound DIDs */}
          <div className="bg-white border border-slate-200 rounded-xl">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Top Inbound DIDs</h3>
              <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                <MoreVertical size={16} />
              </button>
            </div>

            <div className="p-5">
              {topInboundLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : topInboundData && topInboundData.length > 0 ? (
                <>
                  {/* Table Header */}
                  <div className="flex text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                    <div className="flex-1">DID Number</div>
                    <div className="w-24 text-right">Minutes</div>
                    <div className="w-20 text-right">Calls</div>
                  </div>

                  {/* Table Rows */}
                  <div className="space-y-3">
                    {topInboundData.slice(0, 4).map((item) => (
                      <div
                        key={item.did}
                        className="flex items-center cursor-pointer hover:bg-slate-50 -mx-2 px-2 py-1 rounded-lg transition-colors"
                        onClick={() => openCDRDrawer('ORIG')}
                      >
                        <div className="flex-1 font-mono font-semibold text-indigo-600 text-sm">
                          {formatPhoneNumber(item.did)}
                        </div>
                        <div className="w-24 text-right text-sm text-slate-600 font-mono tabular-nums">
                          {formatMinutes(item.minutes).replace('.0', '')}
                        </div>
                        <div className="w-20 text-right text-sm text-slate-600 font-mono tabular-nums">
                          {item.calls.toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Full Report Link */}
                  <button
                    className="mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    onClick={() => openCDRDrawer('ORIG')}
                  >
                    Full Report
                  </button>
                </>
              ) : (
                <p className="text-slate-500 text-center py-8">No inbound calls this month</p>
              )}
            </div>
          </div>

          {/* Top Outbound DIDs */}
          <div className="bg-white border border-slate-200 rounded-xl">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Top Outbound DIDs</h3>
              <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                <MoreVertical size={16} />
              </button>
            </div>

            <div className="p-5">
              {topOutboundLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : topOutboundData && topOutboundData.length > 0 ? (
                <>
                  {/* Table Header */}
                  <div className="flex text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                    <div className="flex-1">DID Number</div>
                    <div className="w-24 text-right">Minutes</div>
                    <div className="w-20 text-right">Calls</div>
                  </div>

                  {/* Table Rows */}
                  <div className="space-y-3">
                    {topOutboundData.slice(0, 4).map((item) => (
                      <div
                        key={item.did}
                        className="flex items-center cursor-pointer hover:bg-slate-50 -mx-2 px-2 py-1 rounded-lg transition-colors"
                        onClick={() => openCDRDrawer('TERM')}
                      >
                        <div className="flex-1 font-mono font-semibold text-indigo-600 text-sm">
                          {formatPhoneNumber(item.did)}
                        </div>
                        <div className="w-24 text-right text-sm text-slate-600 font-mono tabular-nums">
                          {formatMinutes(item.minutes).replace('.0', '')}
                        </div>
                        <div className="w-20 text-right text-sm text-slate-600 font-mono tabular-nums">
                          {item.calls.toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Full Report Link */}
                  <button
                    className="mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    onClick={() => openCDRDrawer('TERM')}
                  >
                    Full Report
                  </button>
                </>
              ) : (
                <p className="text-slate-500 text-center py-8">No outbound calls this month</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Call Detail Records */}
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-5 border-b border-slate-100">
            <div>
              <h3 className="font-semibold text-slate-900">Recent Call Detail Records</h3>
              <p className="text-sm text-slate-500">Last 7 days of global traffic</p>
            </div>

            <div className="flex items-center gap-3">
              {/* Search Input */}
              <div className="relative">
                <Search
                  size={16}
                  strokeWidth={1.5}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  placeholder="Search numbers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-44 pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Call Type Filter */}
              <select
                value={callTypeFilter}
                onChange={(e) => {
                  setCallTypeFilter(e.target.value);
                  setCdrPage(1);
                }}
                className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">All Traffic</option>
                <option value="ORIG">Inbound</option>
                <option value="TERM">Outbound</option>
              </select>

              {/* Full CDR Search Button */}
              <Button
                variant="secondary"
                onClick={() => openCDRDrawer()}
                className="border-slate-200"
              >
                Full CDR Search & Export
              </Button>
            </div>
          </div>

          {/* CDR Table */}
          <div className="overflow-x-auto">
            {cdrListLoading ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : cdrList && cdrList.records.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Date/Time
                    </th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      From
                    </th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      To
                    </th>
                    <th className="text-right py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="text-right py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cdrList.records.map((record) => {
                    const isInbound = ['ORIG', 'Orig-Tiered', '800OrigE', '800ORIGE'].includes(
                      record.call_type
                    );

                    return (
                      <tr key={record.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="py-3 px-5 text-sm text-slate-600 whitespace-nowrap">
                          {new Date(record.start_time).toLocaleString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          })}
                        </td>
                        <td className="py-3 px-5">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold',
                              isInbound
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-violet-50 text-violet-700'
                            )}
                          >
                            {isInbound ? (
                              <PhoneIncoming size={12} strokeWidth={2} />
                            ) : (
                              <PhoneOutgoing size={12} strokeWidth={2} />
                            )}
                            {isInbound ? 'INBOUND' : 'OUTBOUND'}
                          </span>
                        </td>
                        <td className="py-3 px-5 text-sm text-slate-600">
                          {formatPhoneNumber(record.ani)}
                        </td>
                        <td className="py-3 px-5 font-mono font-semibold text-sm text-indigo-600">
                          {formatPhoneNumber(record.dnis)}
                          {record.is_toll_free && (
                            <span className="ml-1.5 text-xs font-normal text-slate-400">(TF)</span>
                          )}
                        </td>
                        <td className="py-3 px-5 text-sm text-slate-600 text-right tabular-nums">
                          {formatDuration(record.bill_minutes)}
                        </td>
                        <td className="py-3 px-5 text-right">
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-cyan-50 text-cyan-700 uppercase">
                            Completed
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 text-slate-500">
                No call records found matching your filters.
              </div>
            )}
          </div>

          {/* Pagination */}
          {cdrList && cdrList.total > 10 && (
            <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100">
              <div className="text-sm text-slate-500">
                Showing {(cdrPage - 1) * 10 + 1} - {Math.min(cdrPage * 10, cdrList.total)} of{' '}
                {cdrList.total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCdrPage((p) => Math.max(1, p - 1))}
                  disabled={cdrPage === 1}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm text-slate-600 px-2">
                  Page {cdrPage} of {cdrList.pages}
                </span>
                <button
                  onClick={() => setCdrPage((p) => Math.min(cdrList.pages, p + 1))}
                  disabled={cdrPage >= cdrList.pages}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CDR Search Drawer */}
      <CDRSearchDrawer
        isOpen={cdrDrawerOpen}
        onClose={() => setCdrDrawerOpen(false)}
        initialCallType={cdrDrawerCallType}
      />
    </div>
  );
}
